// ------------ import external dependencies --------------

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { mountStoreDevtool } from "simple-zustand-devtools";

// ------------ import internal dependencies --------------
import createUserSlice from "./user";
import createAddUserSlice from "./addUser";
import { UserSliceType, AddUserSliceType } from "./type";
import createIncidentSlice from "./incident/incident";
import { IncidentSliceType } from "./incident/incident.type";
import createReportSlice from "./report/report";
import { ReportSliceType } from "./report/report.type";
import createAnalyticsSlice from "./analytics/storeAnalytics";
import { AnalyticsSliceType } from "./analytics/storeAnalytics.types";
import { AdminSliceType } from "./admin/admin.type";
import createAdminSlice from "./admin/admin";
import { FilterSliceType } from "./filters/filters.type";
import createFilterSlice from "./filters/filters";
import createTableFiltersSlice from "./filters/tableFilters";
import { TableFiltersSliceType } from "./filters/tableFilters.type";
import createViewStateSlice from "./viewState/viewState";
import { ViewStateSliceType } from "./viewState/viewState.type";

/**
 * Global store level
 */

const sliceResetFns = new Set<() => void>();

export function addToResetFnsSet(resetFn: () => void) {
  sliceResetFns.add(resetFn);
}

export const resetAllSlices = () => {
  sliceResetFns.forEach((resetFn) => {
    resetFn();
  });
};

export const useStoreWrapper = create<
  UserSliceType & AddUserSliceType & IncidentSliceType
>()(
  persist(
    (...a) => ({
      ...createUserSlice(...a),
      ...createAddUserSlice(...a),
      ...createIncidentSlice(...a),
    }),
    {
      name: "mitigaterisk-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: ({ attachments, ...rest }) => rest,
    }
  )
);

export const clearStorage = () => {
  useStoreWrapper.persist.clearStorage();
  resetAllSlices();
};

export const useReportWrapper = create<ReportSliceType>()(
  persist(
    (...b) => ({
      ...createReportSlice(...b),
    }),
    {
      name: "mitigaterisk-report",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const useAnalyticsWrapper = create<AnalyticsSliceType>()(
  persist(
    (...c) => ({
      ...createAnalyticsSlice(...c),
    }),
    {
      name: "mitigaterisk-analytics",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const useAdminWrapper = create<AdminSliceType>()(
  persist(
    (...d) => ({
      ...createAdminSlice(...d),
    }),
    {
      name: "mitigaterisk-admin",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const useFilterWrapper = create<FilterSliceType>()(
  persist(
    (...e) => ({
      ...createFilterSlice(...e),
    }),
    {
      name: "mitigaterisk-filter",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const useTableFiltersWrapper = create<TableFiltersSliceType>()(
  persist(
    (...f) => ({
      ...createTableFiltersSlice(...f),
    }),
    {
      name: "mitigaterisk-table-filters",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Deliberately not persisted: it holds user-entered filters and report rows (PHI).
export const useViewStateWrapper = create<ViewStateSliceType>()((...g) => ({
  ...createViewStateSlice(...g),
}));

if (process.env.NODE_ENV === "development") {
  mountStoreDevtool("Store", useStoreWrapper);
  mountStoreDevtool("FilterStore", useFilterWrapper);
  mountStoreDevtool("TableFiltersStore", useTableFiltersWrapper);
}
